import { useState, useEffect, useMemo } from "react";
import { BarChart3, Users, TrendingUp, Search, Calendar, Download } from "lucide-react";
import SectionCard from "@/components/SectionCard";
import { formatNum } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export default function Analytics() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filterUser, setFilterUser] = useState("");
  const [filterEvent, setFilterEvent] = useState("");
  const [dateRange, setDateRange] = useState("7d"); // 7d, 30d, 90d

  // Fetch events from Lambda/DynamoDB
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const days = dateRange === "7d" ? 7 : dateRange === "30d" ? 30 : 90;
        const response = await fetch(
          `https://t1tw0cwa38.execute-api.us-east-1.amazonaws.com/analytics?days=${days}`
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        setEvents(data.events || []);
        setError("");
      } catch (err) {
        setError(`Error loading analytics: ${err.message}`);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [dateRange]);

  // Filter events
  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      if (filterUser && e.username !== filterUser) return false;
      if (filterEvent && e.event_type !== filterEvent) return false;
      return true;
    });
  }, [events, filterUser, filterEvent]);

  // Aggregations
  const stats = useMemo(() => {
    const uniqueUsers = new Set(events.map((e) => e.username)).size;
    const logins = events.filter((e) => e.event_type === "login").length;
    const pageViews = events.filter((e) => e.event_type === "page_view").length;
    const queries = events.filter((e) => e.event_type.includes("query")).length;

    // Top pages
    const pageMap = {};
    events
      .filter((e) => e.event_type === "page_view")
      .forEach((e) => {
        pageMap[e.ruta] = (pageMap[e.ruta] || 0) + 1;
      });
    const topPages = Object.entries(pageMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    // Top prestaciones
    const prestMap = {};
    events
      .filter((e) => e.event_type.includes("query"))
      .forEach((e) => {
        const key = e.prestacion_nombre || "(sin nombre)";
        prestMap[key] = (prestMap[key] || 0) + 1;
      });
    const topPrestaciones = Object.entries(prestMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    // Top ISAPREs
    const isapreMap = {};
    events
      .filter((e) => e.event_type.includes("query"))
      .forEach((e) => {
        if (e.isapre) {
          isapreMap[e.isapre] = (isapreMap[e.isapre] || 0) + 1;
        }
      });
    const topIsapres = Object.entries(isapreMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);

    // Events by type
    const eventTypeMap = {};
    events.forEach((e) => {
      eventTypeMap[e.event_type] = (eventTypeMap[e.event_type] || 0) + 1;
    });

    return {
      uniqueUsers,
      logins,
      pageViews,
      queries,
      topPages,
      topPrestaciones,
      topIsapres,
      eventTypeMap,
    };
  }, [events]);

  // Only admin can see analytics
  if (user?.role !== "admin") {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <BarChart3 className="w-12 h-12 text-slate-300 mb-4" />
        <h2 className="text-lg font-semibold text-slate-900">Acceso restringido</h2>
        <p className="text-sm text-slate-500 mt-1">Esta sección está disponible solo para administradores.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Estadísticas de Uso</h1>
        <p className="text-sm text-slate-500 mt-1">Análisis de actividad en ip.garageucc.io</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20"
          >
            <option value="7d">Últimos 7 días</option>
            <option value="30d">Últimos 30 días</option>
            <option value="90d">Últimos 90 días</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Filtrar usuario..."
            value={filterUser}
            onChange={(e) => setFilterUser(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20"
          />
        </div>

        <div className="flex items-center gap-2">
          <Search className="w-4 h-4 text-slate-400" />
          <select
            value={filterEvent}
            onChange={(e) => setFilterEvent(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20"
          >
            <option value="">Todos los eventos</option>
            <option value="login">Login</option>
            <option value="page_view">Page View</option>
            <option value="cotizador_query">Cotizador Query</option>
            <option value="estimador_query">Estimador Query</option>
          </select>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="p-8 text-center text-slate-500">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500 mb-2"></div>
          <p>Cargando datos...</p>
        </div>
      )}

      {!loading && !error && events.length === 0 && (
        <div className="p-8 text-center">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl bg-slate-100 mb-4">
            <BarChart3 className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900">Sin datos aún</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
            Los datos se irán llenando conforme los usuarios naveguen el sitio. Los eventos de login, navegación y consultas se registran automáticamente.
          </p>
        </div>
      )}

      {!loading && !error && events.length > 0 && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Usuarios únicos", value: stats.uniqueUsers, icon: Users, color: "bg-blue-50 text-blue-700" },
              { label: "Logins", value: stats.logins, icon: TrendingUp, color: "bg-emerald-50 text-emerald-700" },
              { label: "Page views", value: stats.pageViews, icon: BarChart3, color: "bg-violet-50 text-violet-700" },
              { label: "Consultas", value: stats.queries, icon: Search, color: "bg-amber-50 text-amber-700" },
            ].map((kpi) => {
              const Icon = kpi.icon;
              return (
                <SectionCard key={kpi.label}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-500 font-medium">{kpi.label}</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{formatNum(kpi.value)}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${kpi.color}`}>
                      <Icon className="w-6 h-6" />
                    </div>
                  </div>
                </SectionCard>
              );
            })}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Pages */}
            <SectionCard title="Páginas más visitadas" subtitle={`Top ${stats.topPages.length}`}>
              <div className="space-y-2">
                {stats.topPages.map(([page, count]) => (
                  <div key={page} className="flex items-center justify-between">
                    <span className="text-xs text-slate-600 truncate">{page}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-2 bg-slate-100 rounded-full" style={{ width: "100px" }}>
                        <div
                          className="h-full bg-violet-500 rounded-full"
                          style={{ width: `${(count / stats.topPages[0][1]) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-semibold text-slate-700 w-8 text-right tabular-nums">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* Top Prestaciones */}
            <SectionCard title="Prestaciones más consultadas" subtitle={`Top ${stats.topPrestaciones.length}`}>
              <div className="space-y-2">
                {stats.topPrestaciones.map(([prest, count]) => (
                  <div key={prest} className="flex items-center justify-between">
                    <span className="text-xs text-slate-600 truncate">{prest}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-2 bg-slate-100 rounded-full" style={{ width: "100px" }}>
                        <div
                          className="h-full bg-emerald-500 rounded-full"
                          style={{ width: `${(count / stats.topPrestaciones[0][1]) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-semibold text-slate-700 w-8 text-right tabular-nums">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* Top ISAPREs */}
            <SectionCard title="ISAPREs más consultadas" subtitle={`Top ${stats.topIsapres.length}`}>
              <div className="space-y-2">
                {stats.topIsapres.map(([isapre, count]) => (
                  <div key={isapre} className="flex items-center justify-between">
                    <span className="text-xs text-slate-600 truncate">{isapre}</span>
                    <div className="flex items-center gap-2">
                      <div className="h-2 bg-slate-100 rounded-full" style={{ width: "100px" }}>
                        <div
                          className="h-full bg-blue-500 rounded-full"
                          style={{ width: `${(count / stats.topIsapres[0][1]) * 100}%` }}
                        />
                      </div>
                      <span className="text-xs font-semibold text-slate-700 w-8 text-right tabular-nums">{count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* Events by Type */}
            <SectionCard title="Eventos por tipo" subtitle="Distribución">
              <div className="space-y-2">
                {Object.entries(stats.eventTypeMap).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <span className="text-xs text-slate-600">{type}</span>
                    <span className="text-xs font-semibold text-slate-700 tabular-nums">{count}</span>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>

          {/* Events Table */}
          <SectionCard title="Eventos recientes" subtitle={`${formatNum(filteredEvents.length)} eventos`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Usuario</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Evento</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Detalles</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvents.slice(0, 50).map((e, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 text-slate-700 font-medium">{e.username}</td>
                      <td className="py-2 px-3">
                        <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">
                          {e.event_type}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-slate-600 text-xs">
                        {e.ruta && <span>{e.ruta}</span>}
                        {e.prestacion_nombre && <span>{e.prestacion_nombre}</span>}
                        {e.isapre && <span> · {e.isapre}</span>}
                      </td>
                      <td className="py-2 px-3 text-slate-500 text-xs">{new Date(e.timestamp).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}
